import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useAuth } from "../features/auth/AuthProvider";
import type { KeybindAction } from "@atrium/shared";
import { applyDefaultFeed, useUI, type Overlay } from "../lib/uiStore";
import { useConnection } from "../lib/online";
import { usePresenceReporter } from "../lib/presence";
import { useRecordRecent } from "../lib/recent";
import { cn } from "../lib/cn";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { toast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { IconClose, IconMessages as IconLetter, IconWifiOff } from "../components/icons";
import { PaletteHost } from "../features/palette/PaletteHost";
import { focusSearchInput, markAllRead } from "../features/palette/actions";
import { Rail } from "./Rail";
import { MobileTabBar } from "./MobileTabBar";
import { Deck, type DeckPane } from "./Deck";
import { RealtimeBridge } from "./RealtimeBridge";
import { PageContextMenu } from "./PageContextMenu";
import { E2eeRedecrypt } from "../features/messages/E2eeUi";
import { AccountNotices } from "../features/staff/AccountNotices";
import { CommunitiesPane } from "../features/communities/CommunitiesPane";
import { FeedPane } from "../features/feed/FeedPane";
import { ComposerDialog } from "../features/posts/Composer";
import { CreateCommunityDialog } from "../features/communities/CreateCommunityDialog";
import { InviteModal } from "../features/communities/InviteCard";
import { VoiceProvider } from "../features/voice/VoiceProvider";
import { PageSpinner } from "../components/ui/Spinner";
import { Wordmark } from "../components/Logo";
import { Button, IconButton } from "../components/ui/Button";
import { api, errorMessage } from "../lib/api";
import { PreferencesEffect, savePrefs, usePrefs } from "../lib/prefs";
import { setFormatPrefs } from "../lib/format";
import { comboFromEvent, findAction, GLOBAL_WHILE_TYPING, isSingleKey, normaliseCombo } from "../features/settings/keybinds";

/** Routes that signed-out visitors may view. */
const PUBLIC_ROUTES = [/^\/u\//, /^\/p\//, /^\/community\//, /^\/invite\//, /^\/t\//];

function deckPaneFor(pathname: string): DeckPane | null {
  if (pathname === "/feed") return "feed";
  if (pathname === "/c" || pathname.startsWith("/c/")) return "communities";
  return null;
}

/** Hide the bottom bar inside full-screen conversations on mobile. */
function hidesTabBar(pathname: string) {
  return /^\/c\/[^/]+\/[^/]+/.test(pathname) || /^\/messages\/[^/]+/.test(pathname);
}

function isTyping(e: KeyboardEvent) {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
}

const VERIFY_DISMISSED = "atrium:verify-email-dismissed";

/** Asks people who haven't confirmed their email to do so. Dismissed until the next visit. */
function VerifyEmailBar() {
  const { me } = useAuth();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(VERIFY_DISMISSED) === "1");
  const [sent, setSent] = useState(false);
  if (!me || me.emailVerified || hidden) return null;
  const resend = async () => {
    try {
      await api.post("/api/auth/verify-email/resend");
      setSent(true);
      toast.success(`Sent a new link to ${me.email}`);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't send the email. Try again later."));
    }
  };
  const dismiss = () => {
    sessionStorage.setItem(VERIFY_DISMISSED, "1");
    setHidden(true);
  };
  return (
    <div role="status" className="pointer-events-none absolute inset-x-0 top-0 z-40 flex justify-center px-3">
      <div className="pointer-events-auto mt-2 flex max-w-full items-center gap-3 rounded-full bg-surface-2 py-1 pl-4 pr-1 text-sm text-fg-2 shadow-pop motion-pop">
        <IconLetter className="icon size-4 shrink-0 text-accent" aria-hidden />
        <span className="truncate">Confirm your email address. We sent a link to {me.email}.</span>
        {!sent && (
          <Button size="sm" variant="ghost" className="h-7 rounded-full" onClick={resend}>
            Resend
          </Button>
        )}
        <IconButton label="Dismiss" icon={IconClose} size="sm" className="rounded-full" onClick={dismiss} />
      </div>
    </div>
  );
}

const OVERLAY_FOR: Partial<Record<KeybindAction, NonNullable<Overlay>>> = { quickSwitcher: "switcher", commandPalette: "palette", shortcutHelp: "shortcuts" };

/**
 * Connection status: offline (no network), connecting, or reconnecting with the
 * attempt count; a short "Back online" confirmation after a real interruption.
 */
function ConnectionBar() {
  const { state, attempt } = useConnection();
  const [shown, setShown] = useState(false);
  const downSince = useRef<number | null>(null);
  useEffect(() => {
    if (state === "online") {
      setShown(false);
      if (downSince.current && Date.now() - downSince.current > 2000) toast.success("Back online");
      downSince.current = null;
      return;
    }
    if (state === "connecting") return;
    downSince.current ??= Date.now();
    // Don't flash the bar for blips shorter than a second.
    const t = setTimeout(() => setShown(true), state === "offline" ? 0 : 1000);
    return () => clearTimeout(t);
  }, [state]);
  if (state === "online" || state === "connecting" || !shown) return null;
  return (
    <div role="status" aria-live="polite" className="pointer-events-none absolute inset-x-0 top-0 z-40 flex justify-center">
      <span className="mt-2 inline-flex items-center gap-2 rounded-full bg-surface-2 px-3 py-1 text-xs font-medium text-fg-2 shadow-pop motion-pop">
        {state === "offline" ? <IconWifiOff className="icon size-4 text-warning" aria-hidden /> : <Spinner className="size-3.5 text-fg-3" />}
        {state === "offline" ? "You're offline. Changes will send when you reconnect." : attempt > 1 ? `Reconnecting… (attempt ${attempt})` : "Reconnecting…"}
      </span>
    </div>
  );
}

/** Older builds kept the theme only on the device; carry it into the synced preference once. */
function useMigrateDeviceTheme(enabled: boolean, serverTheme: string) {
  useEffect(() => {
    if (!enabled) return;
    try {
      if (localStorage.getItem("atrium:theme-synced")) return;
      localStorage.setItem("atrium:theme-synced", "1");
      const old = (JSON.parse(localStorage.getItem("atrium:ui") ?? "{}") as { state?: { theme?: string } }).state?.theme;
      if ((old === "light" || old === "system") && serverTheme === "dark") void savePrefs({ theme: old });
    } catch {
      /* storage unavailable */
    }
  }, [enabled, serverTheme]);
}

export function AppShell() {
  const { me, loading } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { lastCommunityPath, setLastCommunityPath, setLastPath, openComposer, toggleMembers, setOverlay } = useUI();
  const prefs = usePrefs();
  const pane = deckPaneFor(pathname);

  useEffect(() => {
    if (pathname === "/c" || pathname.startsWith("/c/")) setLastCommunityPath(pathname);
    else if (pathname.startsWith("/messages")) setLastPath("messages", pathname);
    else if (pathname === "/explore" && !new URLSearchParams(search).get("q")) setLastPath("explore", pathname + search);
  }, [pathname, search, setLastCommunityPath, setLastPath]);

  // Locale, clock, date and timestamp style for the plain formatting helpers. Set during
  // render (idempotent) so children format with the current preference on their first paint.
  setFormatPrefs({ clock: prefs.clock, dateFormat: prefs.dateFormat, locale: prefs.locale, timestamps: prefs.timestamps, showSeconds: prefs.showSeconds });
  useEffect(() => {
    document.documentElement.lang = prefs.locale;
  }, [prefs.locale]);

  usePresenceReporter(me ? (/^\/c\/([0-9a-f-]{36})/.exec(pathname)?.[1] ?? null) : null);
  useRecordRecent(me ? pathname : "");
  useMigrateDeviceTheme(!!me, prefs.theme);

  useEffect(() => {
    if (me) applyDefaultFeed(prefs.defaultFeed);
  }, [me, prefs.defaultFeed]);

  useEffect(() => {
    if (!me) return;
    const run = (action: KeybindAction) => {
      switch (action) {
        case "goCommunities":
          return navigate(lastCommunityPath || "/c");
        case "goFeed":
          return navigate("/feed");
        case "goExplore":
          return navigate("/explore");
        case "goMessages":
          return navigate("/messages");
        case "goNotifications":
          return navigate("/notifications");
        case "goSettings":
          return navigate("/settings");
        case "newPost":
          return openComposer();
        case "toggleMembers":
          return toggleMembers();
        case "toggleMute":
        case "toggleDeafen":
          window.dispatchEvent(new CustomEvent("atrium:voice", { detail: action }));
          return;
        case "search":
          if (!window.location.pathname.startsWith("/search")) navigate("/search");
          focusSearchInput();
          return;
        case "markAllRead":
          void markAllRead();
          return;
        case "quickSwitcher":
        case "commandPalette":
        case "shortcutHelp": {
          const which = OVERLAY_FOR[action]!;
          setOverlay(useUI.getState().overlay === which ? null : which);
          return;
        }
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const combo = comboFromEvent(e);
      if (!combo) return;
      const action = findAction(prefs.keybinds, combo);
      if (!action) return;
      const overlayAction = GLOBAL_WHILE_TYPING.includes(action) && !isSingleKey(combo);
      if (isTyping(e) && !overlayAction) return;
      // Other dialogs block shortcuts; the palettes can still toggle or replace each other.
      if (document.querySelector("[role=dialog]") && !(overlayAction && useUI.getState().overlay && useUI.getState().overlay !== "status")) return;
      if (isSingleKey(combo) && !prefs.singleKeyShortcuts) return;
      // The voice provider owns the push-to-talk key.
      if (prefs.pushToTalk && normaliseCombo(prefs.pushToTalkKey) === normaliseCombo(combo)) return;
      e.preventDefault();
      if (e.repeat) return;
      run(action);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [me, navigate, lastCommunityPath, openComposer, toggleMembers, setOverlay, prefs.keybinds, prefs.singleKeyShortcuts, prefs.pushToTalk, prefs.pushToTalkKey]);

  if (loading) return <PageSpinner />;

  if (!me) {
    if (!PUBLIC_ROUTES.some((r) => r.test(pathname))) return <Navigate to={`/login?next=${encodeURIComponent(pathname + search)}`} replace />;
    return (
      <div className="flex h-dvh flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-line px-4">
          <Link to="/" aria-label="Atrium home">
            <Wordmark />
          </Link>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate(`/login?next=${encodeURIComponent(pathname)}`)}>
              Sign in
            </Button>
            <Button variant="primary" onClick={() => navigate(`/sign-up?next=${encodeURIComponent(pathname)}`)}>
              Join Atrium
            </Button>
          </div>
        </header>
        <main className="relative min-h-0 flex-1">
          <ErrorBoundary resetKey={pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  return (
    <VoiceProvider>
      <RealtimeBridge />
      <E2eeRedecrypt />
      <PreferencesEffect />
      <PageContextMenu>
        <div className="flex h-dvh flex-col md:flex-row">
          <div className="hidden md:block">
            <Rail />
          </div>
          <main className="relative min-h-0 min-w-0 flex-1">
            <ConnectionBar />
            <VerifyEmailBar />
            <Deck
              active={pane}
              communities={
                <ErrorBoundary label="Communities">
                  <CommunitiesPane />
                </ErrorBoundary>
              }
              feed={
                <ErrorBoundary label="Feed">
                  <FeedPane />
                </ErrorBoundary>
              }
            />
            {!pane && (
              // Keyed by section so moving between sections cross-fades; routes inside a section don't.
              <div key={pathname.split("/")[1]} className="motion-tab absolute inset-0">
                <Outlet />
              </div>
            )}
          </main>
          <div className={cn(hidesTabBar(pathname) && "hidden")}>
            <MobileTabBar />
          </div>
        </div>
      </PageContextMenu>
      <ComposerDialog />
      <CreateCommunityDialog />
      <InviteModal />
      <PaletteHost />
      <AccountNotices />
    </VoiceProvider>
  );
}
